import React, { useEffect, useRef, useState } from "react";
import { CALL_CRM, TASK_CRM } from "../../../utils/constants/assets";
import CircularLoader from "../../../components/Loaders/CircularLoader";
import { STRINGS } from "../strings";
import classes from "./Crm.module.css";
import CreateTaskCrmPopUp from "./components/CreateTaskCrmPopUp";
import ContactDetailEmptyScreen from "./components/ContactDetailEmptyScreen";
import { useLocation } from "react-router-dom";
import ListingTaskCard from "./components/ListingTaskCard";
import TypographyInline from "../../../components/Typography/TypographyInline";
import { fontWeight } from "../../../utils/constants/UI";
import { BRAND } from "../../../utils/constants/colors";
import { Button } from "../../../components/Buttons";
import SkeletonLoaderCRMScreens from "./components/SkeletonLoaderCRMScreens";
import { decompressAndFormat } from "./utils/formatNotesContent";

// Backend stores task body in `notes` for manual user-created tasks, but in
// `body` when the task came from a campaign step (from_campaign === true).
// Either field can be flagged via `is_compressed`. Pick whichever has
// content and run it through the shared decompress + format pipeline.
const resolveTaskContent = (logData) => {
  if (!logData) return logData;
  const hasNotes =
    typeof logData.notes === "string" && logData.notes.trim().length > 0;
  const hasBody =
    typeof logData.body === "string" && logData.body.trim().length > 0;
  if (!hasNotes && !hasBody) return logData;

  const sourceField = hasNotes ? "notes" : "body";
  const formatted = decompressAndFormat(
    logData[sourceField],
    logData.is_compressed,
  );

  return {
    ...logData,
    notes: formatted,
  };
};

const ContactDetailTaskScreen = ({
  contactDetails,
  contactId,
  getTaskLogList = () => {},
  taskListData,
  setTaskListData = () => {},
  totalTasks,
  screenLoading,
  activeCampaigns = [],
  onCall = () => {},
}) => {
  const location = useLocation();
  const [createFlag, setCreateFlag] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  // True while a scroll-triggered "load more" page fetch is in flight. Drives
  // the bottom spinner and (via the ref) prevents a second page from firing
  // before the first one finishes.
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);

  const locationTabs = location?.search?.split("?tabs=");
  const UpdatedLocation = locationTabs[1] === STRINGS.CALLS_SMALL;

  const contactReady = !!Object.keys(contactDetails || {}).length;
  const emptyScreenButtons = UpdatedLocation
    ? [
        {
          id: "make_call",
          label: "Call",
          disabled: !contactReady,
          variant: "primary",
          onClick: () => onCall(),
        },
        {
          id: STRINGS.CALLS_SMALL,
          label: STRINGS.CREATE_CALL, // "Log Call"
          disabled: !contactReady,
          variant: "ghost",
          onClick: () => setCreateFlag(true),
        },
      ]
    : [
        {
          id: STRINGS.TASKS_SMALL,
          label: STRINGS.CREATE_TODO,
          disabled: !contactReady,
          variant: "primary",
          onClick: () => setCreateFlag(true),
        },
      ];

  //Click for open (create Task pop up)
  const createTaskOpenHandler = () => {
    setCreateFlag(true);
  };

  const onScrollApiHandler = async () => {
    const scrollableDiv = document?.getElementsByClassName("allTaskList")[0];
    const isAtBottom =
      scrollableDiv?.scrollTop >=
      scrollableDiv?.scrollHeight - scrollableDiv?.clientHeight;
    // Guard against firing a second page while the first is still loading -
    // the scroll event fires repeatedly near the bottom.
    if (
      isAtBottom &&
      !loadingMoreRef.current &&
      totalTasks > taskListData?.length
    ) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
      const nextPage = pageCount + 1;
      setPageCount(nextPage);
      try {
        await getTaskLogList(true, nextPage);
      } finally {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    getTaskLogList();
  }, [contactDetails]);

  return (
    <div
      className={`${classes.taskScreenMainContainer} allTaskList`}
      id="comments_body"
      onScroll={onScrollApiHandler}
    >
      {screenLoading ? (
        <SkeletonLoaderCRMScreens accordionLengths={5} buttonsLength={1} />
      ) : (
        <>
          {/* Checking the length of the Task list */}
          {taskListData?.length ? (
            <div className={classes.taskListContainer}>
              <div className={classes.HeadTaskContainerSubHead}>
                <TypographyInline
                  label={
                    UpdatedLocation ? STRINGS.ALL_CALLS : STRINGS.ALL_TODOS
                  }
                  size={"md"}
                  fontWeight={fontWeight.SEMIBOLD}
                  color={BRAND.SECONDARY}
                />
                <div className={classes.WIdthButton} style={{ display: "flex", gap: "0.5vw" }}>
                  {UpdatedLocation ? (
                    <>
                      <Button
                        label={"Call"}
                        variant={"primary"}
                        size={"sm36"}
                        onClick={() => onCall()}
                      />
                      <Button
                        label={STRINGS.CREATE_CALL}
                        variant={"ghost"}
                        size={"sm36"}
                        onClick={createTaskOpenHandler}
                      />
                    </>
                  ) : (
                    <Button
                      label={STRINGS.CREATE_TODO}
                      variant={"primary"}
                      size={"sm36"}
                      onClick={createTaskOpenHandler}
                    />
                  )}
                </div>
              </div>
              {/* Sending Task list to the ListingTaskCard*/}

              {taskListData?.map((logData, index) => {
                // A campaign-driven call/task is "pre-scheduled" while its
                // parent campaign is still in Ready To Run (or draft/queued)
                // - the touch hasn't been queued to fire yet. In that state
                // we want the right-side clock/date hidden and the
                // priority/due/time/reminder dropdowns to show "--", since
                // none of those values are real yet.
                const parentCampaign = (activeCampaigns || []).find(
                  (c) => c?.campaign_id === logData?.campaign_id,
                );
                const parentState = String(
                  parentCampaign?.state || "",
                ).trim().toLowerCase();
                const isPreScheduled =
                  !!logData?.from_campaign &&
                  ["ready to run", "ready_to_run", "draft", "queued"].includes(
                    parentState,
                  );
                return (
                  <div key={`log${index}`}>
                    <ListingTaskCard
                      id={`log${index}`}
                      logData={
                        logData?.is_compressed
                          ? // Existing path: notes is compressed → decompress.
                            // Augmented with body-fallback for campaign tasks
                            // where `notes` is null and content is in `body`.
                            resolveTaskContent(logData)
                          : // Even without is_compressed flag, still surface
                            // `body` as `notes` if `notes` is empty so campaign
                            // tasks render their content.
                            resolveTaskContent(logData)
                      }
                      contactDetails={taskListData}
                      contact_id={contactId}
                      setTaskListData={setTaskListData}
                      expanded={!index}
                      // Must be singular to match STRINGS.CALL ("call") /
                      // STRINGS.TASKS - ListingTaskCard does
                      //   const UpdatedLocation = type === STRINGS.CALL;
                      // so plural ("calls"/"tasks") falsely flips into the
                      // task branch and writes step_type=2 for call rows.
                      type={UpdatedLocation ? STRINGS.CALL : STRINGS.TASKS}
                      isPreScheduled={isPreScheduled}
                    />
                  </div>
                );
              })}
              {/* Bottom spinner shown while a "load more" page is fetching. */}
              {loadingMore && (
                <div className={classes.emailLoadMoreLoader}>
                  <CircularLoader size={28} />
                </div>
              )}
            </div>
          ) : (
            <div className={classes.TaskHead}>
              <ContactDetailEmptyScreen
                src={UpdatedLocation ? CALL_CRM : TASK_CRM}
                label={
                  UpdatedLocation ? STRINGS.TASK_LABEL1 : STRINGS.TASK_LABEL
                }
                buttonItems={emptyScreenButtons}
              />
            </div>
          )}
        </>
      )}
      {createFlag && (
        <CreateTaskCrmPopUp
          type={UpdatedLocation ? STRINGS.CALL : STRINGS.TASKS}
          open={createFlag}
          close={setCreateFlag}
          contact_id={contactId}
          setTaskListData={setTaskListData}
          contactDetails={contactDetails}
          getTask={getTaskLogList}
        />
      )}
    </div>
  );
};

export default ContactDetailTaskScreen;
